import { useCallback, useEffect, useRef, useState } from 'react';
import pairTradingApi, { RollMode, RollPlanStatus } from '../../services/pairTradingApi';
import { rollPollIntervalMs } from '../../pages/PairTrading/resolveChartContract';

/**
 * The pair's futures-roll plan, kept fresh while the page is open.
 *
 * Polling rather than pushing, and slowly: the plan changes on human timescales
 * (a date, an acknowledgement) and the countdown it drives is measured in
 * trading days. The one thing that does move on its own is the engine's state —
 * `state: DUE` → `DONE` on the roll day — and a minute of lag there costs the
 * operator nothing.
 *
 * Every date in the payload is resolved server-side against the holiday calendar
 * that is also published to the engine, so nothing here recomputes expiry.
 */
/** App-local minutes since midnight — the unit the plan's `HH:MM` compares in. */
function minutesOfDay(): number {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

export interface UsePairRollPlan {
  status: RollPlanStatus | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  savePlan: (body: {
    mode: RollMode;
    roll_date?: string;
    roll_time?: string;
    acknowledge_settlement?: boolean;
  }) => Promise<void>;
  acknowledge: () => Promise<void>;
  saving: boolean;
}

export function usePairRollPlan(dataConfigId: string | null): UsePairRollPlan {
  const [status, setStatus] = useState<RollPlanStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards against a slow response for a pair the user has already navigated
  // away from overwriting the current one.
  const requestedFor = useRef<string | null>(null);

  const reload = useCallback(async () => {
    if (!dataConfigId) {
      setStatus(null);
      return;
    }
    requestedFor.current = dataConfigId;
    setLoading(true);
    try {
      const next = await pairTradingApi.getRollPlan(dataConfigId);
      if (requestedFor.current !== dataConfigId) return;
      setStatus(next);
      setError(null);
    } catch (e: any) {
      if (requestedFor.current !== dataConfigId) return;
      setError(e?.response?.data?.message ?? e?.message ?? 'could not load the roll plan');
    } finally {
      setLoading(false);
    }
  }, [dataConfigId]);

  // A minute normally; ten seconds inside this pair's own roll window, so the page
  // follows the contract switch within seconds instead of drawing the contract the bot
  // has just left for the rest of the minute. The window comes from the plan — the
  // operator can move `futures_roll_time`, and a constant here would miss it.
  const pollMs = rollPollIntervalMs({ status, nowMinutes: minutesOfDay() });

  useEffect(() => {
    void reload();
    if (!dataConfigId) return;
    const timer = setInterval(() => { void reload(); }, pollMs);
    return () => clearInterval(timer);
  }, [dataConfigId, reload, pollMs]);

  const savePlan = useCallback(
    async (body: { mode: RollMode; roll_date?: string; roll_time?: string; acknowledge_settlement?: boolean }) => {
      if (!dataConfigId) return;
      setSaving(true);
      try {
        // The response IS the new status (the BE re-resolves the plan), so there
        // is no second round-trip and no window where the UI shows a stale date.
        setStatus(await pairTradingApi.setRollPlan(dataConfigId, body));
        setError(null);
      } finally {
        setSaving(false);
      }
    },
    [dataConfigId],
  );

  const acknowledge = useCallback(async () => {
    if (!dataConfigId || !status) return;
    setSaving(true);
    try {
      setStatus(await pairTradingApi.acknowledgeRollWarning(dataConfigId, status.warning.signature));
      setError(null);
    } finally {
      setSaving(false);
    }
  }, [dataConfigId, status]);

  return { status, loading, error, reload, savePlan, acknowledge, saving };
}

export default usePairRollPlan;
